import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";

const initialState = {
  data: [],
  record: null,
  requesting: false,
  error: null,
  action: null,
  query: {
    pageNo: 1,
    pageSize: 50,
    sortBy: "asc",
    sortColumn: "firstName",
    namePattern: "",
  },
  filters: [],
  summary: {},
};

const customIntl = intl();

// Refusals of a user type or of an SSO user's email: the add form and the
// profile cards have no place for them, so they are told.
const USER_TYPE_REFUSALS = [
  "SSO_EMAIL_DUPLICATE",
  "EMAIL_REQUIRED",
  "CANNOT_CHANGE_OWN_USER_TYPE",
  "INVALID_USER_TYPE",
];

// While password sign-in is off, the last active SysRoot user with a password
// cannot be deactivated, deleted or given another role.
const KEEP_SYSROOT_WITH_PASSWORD = "KEEP_SYSROOT_WITH_PASSWORD";

const tellUserTypeRefusal = (payload) => {
  const code = payload?.response;
  if (
    payload?.status === 400 &&
    (USER_TYPE_REFUSALS.includes(code) || code === KEEP_SYSROOT_WITH_PASSWORD)
  ) {
    toast.error(customIntl.formatMessage({ id: code.toLowerCase() }));
  }
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    listRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    listSuccess: (state, action) => {
      state.data = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    listFail: (state, action) => {
      state.data = [];
      state.requesting = false;
      state.error = action.payload;
    },
    adminListRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    createRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "create";
    },
    bulkCreateRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "bulk-create";
    },
    createSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          {
            id:
              state.action === "create"
                ? "user_create_success"
                : "user_bulk_create_success",
          },
          {
            count: action.payload.count || 0,
          }
        )
      );
    },
    createFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      tellUserTypeRefusal(action.payload);
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(
          customIntl.formatMessage({
            id:
              action.payload?.errorCode?.toLowerCase() ||
              "something_else_wrong",
          })
        );
      }
    },
    performActionRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = action.payload.action;
    },
    performActionSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;

      let message = "action_success";
      if (action.payload?.count > 1) {
        message = "action_success_multiple";
      }

      toast.success(
        customIntl.formatMessage(
          { id: message },
          {
            action: customIntl.formatMessage({
              id: `${state.action}_performed`,
            }),
            object: customIntl.formatMessage({ id: "user" }),
            count: action.payload.count || 0,
          }
        )
      );
    },
    performActionFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      tellUserTypeRefusal(action.payload);
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    userDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "user_details";
      // Another user's record (kept from an earlier visit, the store is
      // persisted) must not show, or be saved to, while this one loads.
      if (state.record?.id !== action.payload?.id) {
        state.record = null;
      }
    },
    userDetailsSuccess: (state, action) => {
      state.record = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    userDetailsFail: (state, action) => {
      state.record = null;
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateUserDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateUserDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "user" }),
          }
        )
      );
    },
    updateUserDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      tellUserTypeRefusal(action.payload);
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    setQuery: (state, action) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
    summaryRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "summary";
    },
    summarySuccess: (state, action) => {
      state.summary = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    summaryFail: (state, action) => {
      state.summary = {};
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    downloadTemplateRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "download_template";
    },
    downloadTemplateSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
    },
    // Every refusal is told: a 500 used to reach here as a 400, for which
    // nothing was shown (A-BUG-1). An expired session is left to sign-in.
    downloadTemplateFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload?.status !== 401) {
        toast.error(
          customIntl.formatMessage({ id: "user_template_download_failed" })
        );
      }
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    userExportRequest: (state, action) => {},
    userExportSuccess: (state, action) => {},
    // Every refusal is told: a 500 used to reach here as a 400, for which
    // nothing was shown (sibling of A-BUG-1). An expired session is left to
    // sign-in.
    userExportFail: (state, action) => {
      if (action.payload?.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "user_export_failed" }));
      }
    },
    userPendingAssignmentRequest: (state, action) => {
      state.requesting = true;
      state.action = "pending-assignments";
      state.record = {
        ...state.record,
        pendingAssignments: [],
      };
    },
    userPendingAssignmentSuccess: (state, action) => {
      state.requesting = false;
      state.record = {
        ...state.record,
        pendingAssignments: action.payload.response,
      };
    },
    userPendingAssignmentFail: (state, action) => {
      state.requesting = false;
      state.record = {
        ...state.record,
        pendingAssignments: [],
      };
    },
    markAssignmentAsCompletedRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "mark-as-completed";
    },
    markAssignmentAsCompletedSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "mark_assignment_completed" })
      );
    },
    markAssignmentAsCompletedFail: (state, action) => {
      state.requesting = false;
      // The Mark As Completed dialog stays open and says why (a 400 refuses
      // the picked day).
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
  },
});

export const {
  bulkCreateRequest,
  createFail,
  createRequest,
  createSuccess,
  downloadTemplateFail,
  downloadTemplateRequest,
  downloadTemplateSuccess,
  listFail,
  listRequest,
  listSuccess,
  performActionFail,
  performActionRequest,
  performActionSuccess,
  setFilters,
  setQuery,
  summaryFail,
  summaryRequest,
  summarySuccess,
  updateUserDetailFail,
  updateUserDetailRequest,
  updateUserDetailSuccess,
  userDetailsFail,
  userDetailsRequest,
  userDetailsSuccess,
  adminListRequest,
  userExportFail,
  userExportRequest,
  userExportSuccess,
  userPendingAssignmentRequest,
  userPendingAssignmentFail,
  userPendingAssignmentSuccess,
  markAssignmentAsCompletedFail,
  markAssignmentAsCompletedRequest,
  markAssignmentAsCompletedSuccess,
} = usersSlice.actions;
export default usersSlice.reducer;
